import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Cell,
} from "recharts";
import { useAuth } from "../../../auth/AuthProvider";
import {
    getDailyImpacts,
    getHourlyImpacts,
    type DailyImpact,
    type HourlyImpact,
    type ImpactResponse,
} from "../../../api/impacts";
import { useImpactMode, type ScaleRatios } from "../context/ImpactModeContext";
import { ProfileComparisonChart } from "../../projects/components/ProfileComparisonChart";
import type { ProfileComparisonEntry } from "../../../api/scenarios";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Metric = "generation" | "co2e" | "dalys" | "pdfs" | "economic";
type Granularity = "hourly" | "daily" | "monthly" | "quarterly" | "yearly";

interface AggregatedPoint {
    key: string;       // period key e.g. "2024-03", "2024-Q1", "2024"
    label: string;     // display label
    value: number;
}

// ---------------------------------------------------------------------------
// Metric configuration
// ---------------------------------------------------------------------------

const METRIC_CONFIG: Record<
    Metric,
    {
        label: string;
        color: string;
        dailyKey: keyof DailyImpact;
        hourlyKey: keyof HourlyImpact;
        scaleKey: keyof ScaleRatios | null;   // null = no scaling (generation)
        unit: string;
        format: (v: number) => string;
        totalFormat: (v: number) => string;
    }
> = {
    generation: {
        label: "Generation",
        color: "#00c77b",
        dailyKey: "total_generation_mwh",
        hourlyKey: "generation_mwh",
        scaleKey: null,
        unit: "MWh",
        format: (v) => `${v.toFixed(1)} MWh`,
        totalFormat: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)} GWh` : `${v.toFixed(0)} MWh`),
    },
    co2e: {
        label: "Avoided CO\u2082e",
        color: "#00b4eb",
        dailyKey: "avoided_co2e_kg",
        hourlyKey: "avoided_co2e_kg",
        scaleKey: "co2e",
        unit: "kg",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1000) return `${(v / 1000).toFixed(1)} t`;
            return `${v.toFixed(1)} kg`;
        },
        totalFormat: (v) => `${(Math.abs(v) / 1000).toFixed(1)} t CO\u2082e`,
    },
    dalys: {
        label: "Health (DALYs)",
        color: "#FBBF24",
        dailyKey: "avoided_dalys",
        hourlyKey: "avoided_dalys",
        scaleKey: "dalys",
        unit: "DALYs",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 0.001) return `${v.toFixed(4)}`;
            return `${(v * 1e6).toFixed(1)} \u00b5DALY`;
        },
        totalFormat: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1) return `${v.toFixed(2)} DALYs`;
            return `${(v * 1000).toFixed(1)} mDALY`;
        },
    },
    pdfs: {
        label: "Ecosystem (PDFs)",
        color: "#3B82F6",
        dailyKey: "avoided_pdfs",
        hourlyKey: "avoided_pdfs",
        scaleKey: "pdfs",
        unit: "PDFs",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 0.001) return v.toFixed(4);
            if (abs >= 0.000001) return `${(v * 1000).toFixed(3)} mPDF`;
            return `${(v * 1e6).toFixed(1)} \u00b5PDF`;
        },
        totalFormat: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1) return `${v.toFixed(2)} PDFs`;
            if (abs >= 0.001) return `${(v * 1000).toFixed(1)} mPDF`;
            return `${(v * 1e6).toFixed(0)} \u00b5PDF`;
        },
    },
    economic: {
        label: "Economic Value",
        color: "#22C55E",
        dailyKey: "economic_value_usd",
        hourlyKey: "economic_value_usd",
        scaleKey: "usd",
        unit: "USD",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1000) return `$${(v / 1000).toFixed(1)}k`;
            return `$${v.toFixed(0)}`;
        },
        totalFormat: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
            if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
            return `$${v.toFixed(0)}`;
        },
    },
};

const METRICS: Metric[] = ["generation", "co2e", "dalys", "pdfs", "economic"];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
    { value: "hourly", label: "H" },
    { value: "daily", label: "D" },
    { value: "monthly", label: "M" },
    { value: "quarterly", label: "Q" },
    { value: "yearly", label: "Y" },
];

// ---------------------------------------------------------------------------
// Aggregation helpers
// ---------------------------------------------------------------------------

function quarterOf(month: number): number {
    return Math.ceil(month / 3);
}

function aggregateDaily(
    data: DailyImpact[],
    granularity: "monthly" | "quarterly" | "yearly",
    metric: Metric,
    scaleRatios: ScaleRatios,
): AggregatedPoint[] {
    const cfg = METRIC_CONFIG[metric];
    const ratio = cfg.scaleKey ? scaleRatios[cfg.scaleKey] : 1;

    const buckets = new Map<string, { label: string; sum: number }>();

    for (const d of data) {
        const dt = new Date(d.date + "T00:00:00");
        const y = dt.getFullYear();
        const m = dt.getMonth() + 1;

        let key: string;
        let label: string;

        if (granularity === "monthly") {
            key = `${y}-${String(m).padStart(2, "0")}`;
            label = dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
        } else if (granularity === "quarterly") {
            const q = quarterOf(m);
            key = `${y}-Q${q}`;
            label = `Q${q} '${String(y).slice(2)}`;
        } else {
            key = String(y);
            label = String(y);
        }

        const raw = Math.abs(d[cfg.dailyKey] as number);
        const existing = buckets.get(key);
        if (existing) {
            existing.sum += raw;
        } else {
            buckets.set(key, { label, sum: raw });
        }
    }

    return Array.from(buckets.entries()).map(([key, { label, sum }]) => ({
        key,
        label,
        value: sum * ratio,
    }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Map metric tabs to ProfileComparisonChart domains
const METRIC_TO_COMPARISON_DOMAIN: Partial<Record<Metric, "health" | "biodiversity" | "economic">> = {
    dalys: "health",
    pdfs: "biodiversity",
    economic: "economic",
};

interface ImpactExplorerProps {
    projectId: string;
    isFutureProject?: boolean;
    comparisonProfiles?: ProfileComparisonEntry[];
}

export function ImpactExplorer({ projectId, comparisonProfiles }: ImpactExplorerProps) {
    const { token } = useAuth();
    const { scaleRatios } = useImpactMode();
    const [activeMetric, setActiveMetric] = useState<Metric>(
        comparisonProfiles && comparisonProfiles.length > 1 ? "dalys" : "generation"
    );
    const [granularity, setGranularity] = useState<Granularity | null>(null); // null = auto
    const [hourlyRange, setHourlyRange] = useState<{ start: string; end: string } | null>(null);
    const [dailyViewRange, setDailyViewRange] = useState<{ start: string; end: string } | null>(null);

    const config = METRIC_CONFIG[activeMetric];

    // ---- Daily data (base for D/M/Q/Y) ----
    const { data: dailyData, isLoading: dailyLoading, error: dailyError } = useQuery<ImpactResponse<DailyImpact>>({
        queryKey: ["dailyImpacts", projectId],
        enabled: !!projectId && !!token,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        queryFn: () => getDailyImpacts(token!, projectId, { limit: 3650 }),
    });

    // ---- Hourly data (on-demand) ----
    const { data: hourlyData, isLoading: hourlyLoading } = useQuery<ImpactResponse<HourlyImpact>>({
        queryKey: ["hourlyImpacts", projectId, hourlyRange?.start, hourlyRange?.end],
        enabled: !!projectId && !!token && !!hourlyRange,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        queryFn: () =>
            getHourlyImpacts(token!, projectId, {
                startDate: hourlyRange!.start,
                endDate: hourlyRange!.end,
            }),
    });

    // ---- Hourly existence probe ----
    // The daily series is served from the `daily_project_impacts` continuous
    // aggregate, which can lag the raw hourly_impacts table (bulk historical
    // loads, or a refresh that hasn't run yet). If the daily query comes back
    // empty we cheaply probe hourly (limit=1) so a lagging cagg degrades to
    // "data exists, daily rollup pending" instead of a false "no data" wall.
    const dailyEmpty = !dailyLoading && (!dailyData?.data || dailyData.data.length === 0);
    const { data: hourlyProbe } = useQuery<ImpactResponse<HourlyImpact>>({
        queryKey: ["hourlyProbe", projectId],
        enabled: !!projectId && !!token && dailyEmpty,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        queryFn: () => getHourlyImpacts(token!, projectId, { limit: 1 }),
    });
    const hasHourlyData =
        (hourlyProbe?.total_records ?? 0) > 0 ||
        (!!hourlyData?.data && hourlyData.data.length > 0);

    // ---- Smart default granularity ----
    const effectiveGranularity = useMemo<Granularity>(() => {
        if (granularity) return granularity;
        if (!dailyData?.data || dailyData.data.length === 0) return "daily";
        return dailyData.data.length > 365 ? "monthly" : "daily";
    }, [granularity, dailyData]);

    // ---- Date range subtitle ----
    const dateRange = useMemo(() => {
        if (!dailyData?.data || dailyData.data.length < 2) return null;
        const first = new Date(dailyData.data[0].date + "T00:00:00");
        const last = new Date(dailyData.data[dailyData.data.length - 1].date + "T00:00:00");
        const fmt: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" };
        return `${first.toLocaleDateString("en-US", fmt)} \u2013 ${last.toLocaleDateString("en-US", fmt)}`;
    }, [dailyData]);

    // ---- Multi-year check for daily view ----
    const isMultiYear = useMemo(() => {
        if (!dailyData?.data || dailyData.data.length < 2) return false;
        return dailyData.data[0].date.slice(0, 4) !== dailyData.data[dailyData.data.length - 1].date.slice(0, 4);
    }, [dailyData]);

    // ---- Available years for date picker ----
    const availableYears = useMemo(() => {
        if (!dailyData?.data || dailyData.data.length < 2) return [];
        const years = new Set(dailyData.data.map((d) => d.date.slice(0, 4)));
        return Array.from(years).sort();
    }, [dailyData]);

    // ---- Filter daily data by view range ----
    const filteredDailyData = useMemo(() => {
        if (!dailyData?.data) return [];
        if (!dailyViewRange) return dailyData.data;
        return dailyData.data.filter(
            (d) => d.date >= dailyViewRange.start && d.date <= dailyViewRange.end
        );
    }, [dailyData, dailyViewRange]);

    // ---- Build chart data ----
    const ratio = config.scaleKey ? scaleRatios[config.scaleKey] : 1;

    const chartData = useMemo<AggregatedPoint[]>(() => {
        if (effectiveGranularity === "hourly") {
            if (!hourlyData?.data) return [];
            return hourlyData.data.map((h) => {
                const dt = new Date(h.timestamp);
                return {
                    key: h.timestamp,
                    label: dt.toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                    }),
                    value: Math.abs(h[config.hourlyKey] as number) * ratio,
                };
            });
        }

        if (!filteredDailyData.length) return [];

        if (effectiveGranularity === "daily") {
            // For multi-year daily with >400 points, show as bars not area
            const showingMultiYear = isMultiYear && !dailyViewRange;
            return filteredDailyData.map((d) => {
                const dt = new Date(d.date + "T00:00:00");
                const displayDate = showingMultiYear
                    ? dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
                    : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                return {
                    key: d.date,
                    label: displayDate,
                    value: Math.abs(d[config.dailyKey] as number) * ratio,
                };
            });
        }

        return aggregateDaily(filteredDailyData, effectiveGranularity, activeMetric, scaleRatios);
    }, [filteredDailyData, hourlyData, effectiveGranularity, activeMetric, config, scaleRatios, isMultiYear, dailyViewRange, ratio]);

    // ---- Stats ----
    const stats = useMemo(() => {
        if (!chartData.length) return null;
        const values = chartData.map((d) => d.value);
        const total = values.reduce((s, v) => s + v, 0);
        const avg = total / values.length;
        const peak = Math.max(...values);
        const peakLabel = chartData[values.indexOf(peak)]?.label;

        const periodLabel: Record<Granularity, string> = {
            hourly: "Hours",
            daily: "Days",
            monthly: "Months",
            quarterly: "Quarters",
            yearly: "Years",
        };
        const avgLabel: Record<Granularity, string> = {
            hourly: "Hourly Avg",
            daily: "Daily Avg",
            monthly: "Monthly Avg",
            quarterly: "Quarterly Avg",
            yearly: "Yearly Avg",
        };

        return { total, avg, peak, peakLabel, count: values.length, periodLabel: periodLabel[effectiveGranularity], avgLabel: avgLabel[effectiveGranularity] };
    }, [chartData, effectiveGranularity]);

    // ---- Hourly drill-down handlers ----
    const handleDailyClick = useCallback(
        (date: string) => {
            if (effectiveGranularity !== "daily") return;
            const center = new Date(date + "T00:00:00");
            const start = new Date(center);
            start.setDate(start.getDate() - 3);
            const end = new Date(center);
            end.setDate(end.getDate() + 3);
            const fmt = (d: Date) => d.toISOString().slice(0, 10);
            setHourlyRange({ start: fmt(start), end: fmt(end) });
            setGranularity("hourly");
        },
        [effectiveGranularity],
    );

    const shiftHourlyWindow = useCallback(
        (direction: -1 | 1) => {
            if (!hourlyRange) return;
            const shift = 7 * direction; // shift by 7 days
            const start = new Date(hourlyRange.start + "T00:00:00");
            const end = new Date(hourlyRange.end + "T00:00:00");
            start.setDate(start.getDate() + shift);
            end.setDate(end.getDate() + shift);
            const fmt = (d: Date) => d.toISOString().slice(0, 10);
            setHourlyRange({ start: fmt(start), end: fmt(end) });
        },
        [hourlyRange],
    );

    const backToDaily = useCallback(() => {
        setGranularity(null);
        setHourlyRange(null);
    }, []);

    // ---- Profile comparison mode ----
    const comparisonDomain = METRIC_TO_COMPARISON_DOMAIN[activeMetric];
    const showComparison = !!comparisonProfiles && comparisonProfiles.length > 1 && !!comparisonDomain;

    // ---- Chart type ----
    // Use bar chart for aggregations AND for daily view with too many points (>400 = multi-year without filter)
    const tooManyDailyPoints = effectiveGranularity === "daily" && chartData.length > 400;
    const useBarChart = effectiveGranularity === "monthly" || effectiveGranularity === "quarterly" || effectiveGranularity === "yearly" || tooManyDailyPoints;

    // ---- Loading / error / empty states ----
    const isLoading = dailyLoading || (effectiveGranularity === "hourly" && hourlyLoading);
    const hasComparisonData = !!comparisonProfiles && comparisonProfiles.length > 1;
    const hasDailyData = !!dailyData?.data && dailyData.data.length > 0;

    // Only show empty state if there's genuinely no time-series data at all
    // (no daily rollup, no raw hourly) AND no comparison data. A populated
    // hourly table with an empty daily cagg means the rollup is just pending,
    // not that data is missing.
    if (!isLoading && !hasDailyData && !hasHourlyData && !hasComparisonData) {
        return (
            <div className="rounded-xl bg-surface-700 border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-2">Impact Explorer</h3>
                <p className="text-sm text-text-secondary">
                    No time-series impact data available yet. Re-run the model to generate data.
                </p>
            </div>
        );
    }

    // Hourly exists but the daily rollup hasn't materialized yet — the daily
    // chart can't render, so explain rather than show a misleading blank.
    if (!isLoading && !hasDailyData && hasHourlyData && !hasComparisonData) {
        return (
            <div className="rounded-xl bg-surface-700 border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-2">Impact Explorer</h3>
                <p className="text-sm text-text-secondary">
                    Hourly impact data is available; the daily rollup is still being
                    prepared and will appear here shortly.
                </p>
            </div>
        );
    }

    if (isLoading && chartData.length === 0 && !hasComparisonData) {
        return (
            <div className="rounded-xl bg-surface-700 border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Impact Explorer</h3>
                <div className="h-64 flex items-center justify-center">
                    <div className="animate-pulse text-sm text-text-secondary">Loading time-series data...</div>
                </div>
            </div>
        );
    }

    if (dailyError && !hasComparisonData) {
        return (
            <div className="rounded-xl bg-surface-700 border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-2">Impact Explorer</h3>
                <p className="text-sm text-health-400">Failed to load time-series data.</p>
            </div>
        );
    }

    // Compute XAxis minTickGap
    const minTickGap = effectiveGranularity === "hourly" ? 60 : effectiveGranularity === "daily" && isMultiYear ? 60 : 40;

    return (
        <div className="rounded-xl bg-surface-700 border border-border p-6">
            {/* Header row */}
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">Impact Explorer</h3>
                {dateRange && <span className="text-xs text-text-secondary">{dateRange}</span>}
            </div>

            {/* Metric tabs + granularity selector */}
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                {/* Metric tabs */}
                <div className="flex gap-1 flex-wrap">
                    {METRICS.map((m) => (
                        <button
                            key={m}
                            onClick={() => setActiveMetric(m)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                activeMetric === m
                                    ? "text-white"
                                    : "bg-surface-600 text-text-secondary hover:text-text-primary"
                            }`}
                            style={
                                activeMetric === m
                                    ? { backgroundColor: METRIC_CONFIG[m].color + "33", color: METRIC_CONFIG[m].color }
                                    : undefined
                            }
                        >
                            {METRIC_CONFIG[m].label}
                        </button>
                    ))}
                </div>

                {/* Granularity + date range controls — hidden when showing comparison chart or no time-series data */}
                {!showComparison && hasDailyData && (
                    <div className="flex items-center gap-3">
                        {/* Year / range picker for multi-year projects */}
                        {availableYears.length > 1 && effectiveGranularity !== "hourly" && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setDailyViewRange(null)}
                                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                        !dailyViewRange
                                            ? "bg-quantum-500/20 text-quantum-400"
                                            : "bg-surface-600 text-text-muted hover:text-text-secondary"
                                    }`}
                                >
                                    All
                                </button>
                                {availableYears.map((yr) => (
                                    <button
                                        key={yr}
                                        onClick={() =>
                                            setDailyViewRange({ start: `${yr}-01-01`, end: `${yr}-12-31` })
                                        }
                                        className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                            dailyViewRange?.start?.startsWith(yr)
                                                ? "bg-quantum-500/20 text-quantum-400"
                                                : "bg-surface-600 text-text-muted hover:text-text-secondary"
                                        }`}
                                    >
                                        {yr}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* Granularity selector */}
                        <div className="flex items-center bg-surface-600 rounded-md p-0.5">
                            {GRANULARITY_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => {
                                        if (opt.value === "hourly" && effectiveGranularity !== "hourly") {
                                            // Default to latest 7 days of daily data for hourly
                                            const source = filteredDailyData.length > 0 ? filteredDailyData : dailyData?.data || [];
                                            if (source.length > 0) {
                                                const last = source[source.length - 1].date;
                                                const end = new Date(last + "T00:00:00");
                                                const start = new Date(end);
                                                start.setDate(start.getDate() - 6);
                                                const fmt = (d: Date) => d.toISOString().slice(0, 10);
                                                setHourlyRange({ start: fmt(start), end: fmt(end) });
                                            }
                                        } else if (opt.value !== "hourly") {
                                            setHourlyRange(null);
                                        }
                                        setGranularity(opt.value);
                                    }}
                                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                        effectiveGranularity === opt.value
                                            ? "bg-surface-500 text-text-primary shadow-sm"
                                            : "text-text-muted hover:text-text-secondary"
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Profile Comparison Chart (replaces time-series for dalys/pdfs/economic when data available) */}
            {showComparison ? (
                <div className="mt-1">
                    <ProfileComparisonChart
                        profiles={comparisonProfiles!}
                        domain={comparisonDomain!}
                    />
                </div>
            ) : !hasDailyData ? (
                <div className="h-48 flex items-center justify-center">
                    <p className="text-sm text-text-secondary">
                        No time-series data available for this metric. Switch to Health, Ecosystem, or Economic to see profile comparison.
                    </p>
                </div>
            ) : (
                <>
                    {/* Hourly navigation with date picker */}
                    {effectiveGranularity === "hourly" && hourlyRange && (
                        <div className="flex items-center gap-3 mb-3 text-xs text-text-secondary">
                            <button onClick={() => shiftHourlyWindow(-1)} className="px-2 py-1 rounded bg-surface-600 hover:bg-surface-500 hover:text-text-primary transition-colors">
                                &#9664;
                            </button>
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="date"
                                    value={hourlyRange.start}
                                    min={dailyData?.data?.[0]?.date}
                                    max={dailyData?.data?.[dailyData.data.length - 1]?.date}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            const start = new Date(e.target.value + "T00:00:00");
                                            const end = new Date(start);
                                            end.setDate(end.getDate() + 6);
                                            const fmt = (d: Date) => d.toISOString().slice(0, 10);
                                            setHourlyRange({ start: fmt(start), end: fmt(end) });
                                        }
                                    }}
                                    className="bg-surface-600 border border-border rounded px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-quantum-500 [color-scheme:dark]"
                                />
                                <span className="text-text-muted">&ndash;</span>
                                <input
                                    type="date"
                                    value={hourlyRange.end}
                                    min={hourlyRange.start}
                                    max={dailyData?.data?.[dailyData.data.length - 1]?.date}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setHourlyRange({ start: hourlyRange.start, end: e.target.value });
                                        }
                                    }}
                                    className="bg-surface-600 border border-border rounded px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-quantum-500 [color-scheme:dark]"
                                />
                            </div>
                            <button onClick={() => shiftHourlyWindow(1)} className="px-2 py-1 rounded bg-surface-600 hover:bg-surface-500 hover:text-text-primary transition-colors">
                                &#9654;
                            </button>
                            {/* Quick range buttons */}
                            <div className="flex gap-1 ml-2">
                                {[
                                    { label: "1D", days: 1 },
                                    { label: "1W", days: 7 },
                                    { label: "2W", days: 14 },
                                    { label: "1M", days: 30 },
                                ].map(({ label, days }) => (
                                    <button
                                        key={label}
                                        onClick={() => {
                                            const end = new Date(hourlyRange.end + "T00:00:00");
                                            const start = new Date(end);
                                            start.setDate(start.getDate() - days + 1);
                                            const fmt = (d: Date) => d.toISOString().slice(0, 10);
                                            setHourlyRange({ start: fmt(start), end: fmt(end) });
                                        }}
                                        className="px-1.5 py-0.5 rounded text-[10px] bg-surface-600 text-text-muted hover:text-text-primary hover:bg-surface-500 transition-colors"
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <button onClick={backToDaily} className="ml-auto text-quantum-400 hover:text-quantum-300 transition-colors">
                                Back to Daily
                            </button>
                        </div>
                    )}

                    {/* Chart */}
                    <ResponsiveContainer width="100%" height={300}>
                        {useBarChart ? (
                            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                                    tickLine={false}
                                    axisLine={{ stroke: "#334155" }}
                                    interval="preserveStartEnd"
                                    minTickGap={40}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v) => config.format(v)}
                                    width={70}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#1e293b",
                                        border: "1px solid #334155",
                                        borderRadius: "8px",
                                        fontSize: "12px",
                                        color: "#e2e8f0",
                                    }}
                                    formatter={(value: number) => [config.format(value), config.label]}
                                    labelFormatter={(label) => String(label)}
                                />
                                <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={40}>
                                    {chartData.map((_, i) => (
                                        <Cell key={i} fill={config.color} fillOpacity={0.85} />
                                    ))}
                                </Bar>
                            </BarChart>
                        ) : (
                            <AreaChart
                                data={chartData}
                                margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                                onClick={(e: any) => {
                                    if (effectiveGranularity === "daily" && e?.activePayload?.[0]?.payload?.key) {
                                        handleDailyClick(e.activePayload[0].payload.key);
                                    }
                                }}
                            >
                                <defs>
                                    <linearGradient id={`ie-grad-${activeMetric}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={config.color} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={config.color} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                                    tickLine={false}
                                    axisLine={{ stroke: "#334155" }}
                                    interval="preserveStartEnd"
                                    minTickGap={minTickGap}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v) => config.format(v)}
                                    width={70}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#1e293b",
                                        border: "1px solid #334155",
                                        borderRadius: "8px",
                                        fontSize: "12px",
                                        color: "#e2e8f0",
                                    }}
                                    formatter={(value: number) => [config.format(value), config.label]}
                                    labelFormatter={(_, payload) => {
                                        const key = payload?.[0]?.payload?.key;
                                        if (!key) return "";
                                        if (effectiveGranularity === "hourly") {
                                            return new Date(key).toLocaleString("en-US", {
                                                weekday: "short",
                                                month: "short",
                                                day: "numeric",
                                                hour: "numeric",
                                                minute: "2-digit",
                                            });
                                        }
                                        return new Date(key + "T00:00:00").toLocaleDateString("en-US", {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric",
                                        });
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={config.color}
                                    fill={`url(#ie-grad-${activeMetric})`}
                                    strokeWidth={1.5}
                                    dot={false}
                                    activeDot={{ r: 3, fill: config.color }}
                                    style={effectiveGranularity === "daily" ? { cursor: "pointer" } : undefined}
                                />
                            </AreaChart>
                        )}
                    </ResponsiveContainer>

                    {/* Click hint for daily view (only when not too many points) */}
                    {effectiveGranularity === "daily" && !tooManyDailyPoints && (
                        <p className="text-[10px] text-text-muted text-center mt-1">
                            Click a day to drill down to hourly data
                        </p>
                    )}
                    {/* Hint for multi-year daily with too many points */}
                    {tooManyDailyPoints && availableYears.length > 1 && (
                        <p className="text-[10px] text-text-muted text-center mt-1">
                            Select a year above to see daily detail and enable hourly drill-down
                        </p>
                    )}

                    {/* Summary stats */}
                    {stats && (
                        <div className="mt-4 grid grid-cols-4 gap-4 text-center">
                            <div>
                                <p className="text-xs text-text-secondary">Total</p>
                                <p className="text-sm font-semibold" style={{ color: config.color }}>
                                    {config.totalFormat(stats.total)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-text-secondary">{stats.avgLabel}</p>
                                <p className="text-sm font-semibold text-text-primary">{config.format(stats.avg)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-text-secondary">Peak</p>
                                <p className="text-sm font-semibold text-text-primary">{config.format(stats.peak)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-text-secondary">{stats.periodLabel}</p>
                                <p className="text-sm font-semibold text-text-primary">{stats.count}</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
